/**
 * The production's as-run log (PLAN §5.4): append-only events, oldest
 * first, a page at a time — "Load more" continues from the last page's
 * cursor; "Refresh" re-reads for events added since. `events`, `loading` and
 * `error` are independent (a refetch keeps the list on screen).
 */
import type { AsRunEventWire } from '@acp/api-contract/playout.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { RefreshCwIcon } from 'lucide-react';
import { formatTimeOfDay } from '../../lib/playout/format.js';
import { Button } from '../ui/button.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AsRunListProps = {
  readonly events: readonly AsRunEventWire[];
  readonly loading: boolean;
  readonly error: string | null;
  /** More events are already there beyond the pages read. */
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
  readonly onRefresh: () => void;
};

/** The detail an as-run event carries, compactly: `type=take · part=Headlines`. */
function detailText(detail: Readonly<Record<string, JsonValue>>): string {
  return Object.entries(detail)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' · ');
}

export function AsRunList({ events, loading, error, hasMore, onLoadMore, onRefresh }: AsRunListProps) {
  return (
    <div className="grid gap-3" data-testid="asrun-list">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground" data-testid="asrun-count">
          {events.length} event{events.length === 1 ? '' : 's'}
          {hasMore ? ' (more to load)' : ''}
        </span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={onRefresh} disabled={loading}>
          <RefreshCwIcon />
          Refresh
        </Button>
      </div>
      {error === null ? null : (
        <p role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive">
          Could not load the as-run log: {error}
        </p>
      )}
      {events.length === 0 && !loading && error === null ? <p className="py-6 text-center text-sm text-muted-foreground">No events yet.</p> : null}
      {events.length === 0 && loading ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> : null}
      {events.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Time</TableHead>
              <TableHead className="w-40">Event</TableHead>
              <TableHead className="w-32">By</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.eventId} data-testid="asrun-event" data-type={e.type}>
                <TableCell className="font-mono text-xs tabular-nums">{formatTimeOfDay(e.at)}</TableCell>
                <TableCell className="font-medium">{e.type}</TableCell>
                <TableCell className="truncate text-muted-foreground">{e.userId ?? '–'}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{detailText(e.detail)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {hasMore ? (
        <Button variant="outline" onClick={onLoadMore} disabled={loading} data-testid="asrun-more">
          {loading ? 'Loading…' : 'Load more'}
        </Button>
      ) : null}
    </div>
  );
}
